import { useState } from 'react';
import { Paper, Title, Text, Group, Badge, Table, Stack, SegmentedControl, Button } from '@mantine/core';
import { IconCalendar, IconList, IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { format, isBefore, isAfter, startOfDay } from 'date-fns';
import type { AssetAuditEvent } from '@/types/models';

interface AuditCalendarProps {
  audits: AssetAuditEvent[];
}

export function AuditCalendar({ audits }: AuditCalendarProps) {
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const navigate = useNavigate();

  // Filter for upcoming and in-progress audits
  const today = startOfDay(new Date());
  const upcomingAudits = audits
    .filter((audit) => {
      if (audit.status === 'completed' || audit.status === 'cancelled') return false;
      if (audit.due_by_date) {
        return isAfter(new Date(audit.due_by_date), today) || audit.status === 'in_progress';
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = a.due_by_date || a.audit_date || a.created_at;
      const dateB = b.due_by_date || b.audit_date || b.created_at;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    })
    .slice(0, 10);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'gray';
      case 'in_progress':
        return 'blue';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const isOverdue = (audit: AssetAuditEvent) => {
    if (!audit.due_by_date) return false;
    return isBefore(new Date(audit.due_by_date), today) && audit.status !== 'completed';
  };

  if (audits.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Group justify="apart" mb="md">
          <Title order={3}>Upcoming Audits</Title>
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/audits')}
          >
            Create Audit
          </Button>
        </Group>
        <Text c="dimmed" ta="center" py="xl">
          No upcoming audits scheduled
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="apart">
          <Title order={3}>Upcoming Audits</Title>
          <Group>
            <SegmentedControl
              size="xs"
              value={viewMode}
              onChange={(value) => setViewMode(value as 'table' | 'calendar')}
              data={[
                { label: <IconList size={16} />, value: 'table' },
                { label: <IconCalendar size={16} />, value: 'calendar' },
              ]}
            />
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => navigate('/audits')}
            >
              Create
            </Button>
          </Group>
        </Group>

        {viewMode === 'table' ? (
          upcomingAudits.length > 0 ? (
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Audit Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Due Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {upcomingAudits.map((audit) => (
                  <Table.Tr
                    key={audit.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/audits/${audit.id}`)}
                  >
                    <Table.Td>
                      <Text fw={500}>{audit.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Badge size="sm" variant="light" color={getStatusColor(audit.status)}>
                          {audit.status}
                        </Badge>
                        {isOverdue(audit) && (
                          <Badge size="sm" color="red">
                            Overdue
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {audit.due_by_date ? (
                        <Text size="sm" c={isOverdue(audit) ? 'red' : undefined}>
                          {format(new Date(audit.due_by_date), 'MMM dd, yyyy')}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          No due date
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text c="dimmed" ta="center" py="xl">
              No upcoming audits
            </Text>
          )
        ) : (
          <Stack gap="sm">
            {upcomingAudits.length > 0 ? (
              upcomingAudits.map((audit) => (
                <Paper
                  key={audit.id}
                  p="sm"
                  withBorder
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/audits/${audit.id}`)}
                >
                  <Group justify="apart">
                    <div>
                      <Text fw={500} size="sm">
                        {audit.name}
                      </Text>
                      {audit.due_by_date && (
                        <Text size="xs" c={isOverdue(audit) ? 'red' : 'dimmed'}>
                          Due: {format(new Date(audit.due_by_date), 'MMM dd, yyyy')}
                        </Text>
                      )}
                    </div>
                    <Group gap="xs">
                      <Badge size="sm" variant="light" color={getStatusColor(audit.status)}>
                        {audit.status}
                      </Badge>
                      {isOverdue(audit) && (
                        <Badge size="sm" color="red">
                          Overdue
                        </Badge>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No upcoming audits
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
